import { renderMessageText } from './message-text-renderer.js'
import { renderLucideIcon } from '../utils/lucide-icons.js'

export const DEBRIEF_CHAPTER_MAX_LENGTH = 20000

// Deliberately limited Markdown: no raw HTML, links or embedded resources.
export function renderDebriefMarkdown(markdown) {
  var lines = String(markdown || '').replace(/\r\n?/g, '\n').split('\n')
  var html = '', paragraph = [], list = [], listType = ''
  function flushParagraph() {
    if (paragraph.length) html += '<p>' + renderMessageText(paragraph.join('\n')) + '</p>'
    paragraph = []
  }
  function flushList() {
    if (list.length) html += '<' + listType + '>' + list.map(function (line) { return '<li>' + renderMessageText(line) + '</li>' }).join('') + '</' + listType + '>'
    list = []; listType = ''
  }
  lines.forEach(function (line) {
    var heading = line.match(/^#{1,6}\s+(.+)$/)
    var bullet = line.match(/^\s*(?:[-*+•]|\d+[.)])\s+(.+)$/)
    if (heading) {
      flushParagraph(); flushList()
      html += '<h4>' + renderMessageText(heading[1]) + '</h4>'
    } else if (bullet) {
      flushParagraph()
      var type = /^\s*\d/.test(line) ? 'ol' : 'ul'
      if (listType && listType !== type) flushList()
      listType = type; list.push(bullet[1])
    } else if (!line.trim()) {
      flushParagraph(); flushList()
    } else {
      flushList(); paragraph.push(line)
    }
  })
  flushParagraph(); flushList()
  return html || '<p>Ce chapitre est vide.</p>'
}

export function getDebriefChapterMarkdown(ui, key, blockKeys) {
  var edit = (ui.chapterEdits || []).find(function (item) { return item.key === key })
  if (edit && typeof edit.markdown === 'string') return edit.markdown
  var chapter = ((ui.presentation || {}).chapters || []).find(function (item) { return item.key === key })
  if (chapter) return (chapter.sections || []).map(function (section) {
    return '## ' + section.title + '\n\n' + (section.paragraphs || []).join('\n\n')
  }).join('\n\n')
  return blockKeys.map(function (blockKey) {
    var block = (ui.blocks || []).find(function (item) { return item.key === blockKey })
    if (!block) return ''
    var items = Array.isArray(block.content) ? block.content : [block.content]
    return '## ' + block.title + '\n\n' + items.map(function (item) { return typeof item === 'string' ? item : String(item && (item.label || item.text) || '') }).join('\n\n')
  }).filter(Boolean).join('\n\n')
}

export function renderDebriefChapterEditor(chapter, editorId, escapeHtml) {
  return '<div class="wc-debrief-chapter-heading"><h3>' + escapeHtml(chapter.title) + '</h3>' +
    '<button type="button" class="wc-debrief-edit-button" data-debrief-edit aria-label="Modifier ' + escapeHtml(chapter.title) + '">' + renderLucideIcon('pencil', { size: 20 }) + 'Modifier</button></div>' +
    '<div data-debrief-chapter-body>' + chapter.body + '</div>' +
    '<div data-debrief-editor hidden><label for="' + editorId + '">Modifier le chapitre en Markdown</label>' +
    '<p class="sectionhelp">## Titre, **texte en gras**, - liste. Sépare les paragraphes par une ligne vide.</p>' +
    '<textarea id="' + editorId + '" class="wc-debrief-markdown-editor" data-debrief-markdown maxlength="' + DEBRIEF_CHAPTER_MAX_LENGTH + '" rows="12">' + escapeHtml(chapter.markdown) + '</textarea>' +
    '<div class="wc-debrief-editor-actions"><button type="button" class="wc-debrief-edit-button primary" data-debrief-apply>Enregistrer les modifications</button>' +
    '<button type="button" class="wc-debrief-edit-button" data-debrief-cancel>Annuler</button></div></div>'
}

export function bindDebriefChapterEditor(root, ui, handlers) {
  var changes = {}
  var editors = []
  var statuses = Array.from(root.querySelectorAll('[data-debrief-edit-status]'))
  function status(text) { statuses.forEach(function (node) { node.textContent = text }) }
  function updateStatus() {
    var pending = editors.some(function (editor) { return editor.input.value !== editor.original })
    var dirty = pending || Object.keys(changes).length > 0
    status(dirty ? 'Modifications locales — clique sur « Enregistrer le bilan » pour les sauvegarder.' : '')
    root.querySelectorAll('[data-capability-refresh]').forEach(function (button) {
      button.disabled = dirty
      button.title = dirty ? 'Enregistre le bilan avant de le rafraîchir.' : 'Rafraîchir le bilan'
    })
  }
  root.querySelectorAll('[data-debrief-chapter-panel]').forEach(function (panel) {
    var input = panel.querySelector('[data-debrief-markdown]')
    if (!input) return
    var key = panel.getAttribute('data-debrief-chapter-panel')
    var editor = { input: input, original: input.value, applied: input.value, key: key }
    var form = panel.querySelector('[data-debrief-editor]')
    var body = panel.querySelector('[data-debrief-chapter-body]')
    var button = panel.querySelector('[data-debrief-edit]')
    button.disabled = !ui.reviewIdentity
    if (!ui.reviewIdentity) button.title = 'Rouvre le bilan pour pouvoir le modifier.'
    var initialBody = body.innerHTML
    function close() { form.hidden = true; body.hidden = false; button.hidden = false; button.focus() }
    editor.apply = function () {
      if (input.value.length > DEBRIEF_CHAPTER_MAX_LENGTH) {
        status('Le chapitre dépasse la limite de 20 000 caractères.')
        return false
      }
      editor.applied = input.value
      if (input.value === editor.original) {
        delete changes[key]
        body.innerHTML = initialBody
      } else {
        changes[key] = input.value
        body.innerHTML = renderDebriefMarkdown(input.value)
      }
      close(); updateStatus()
      return true
    }
    editors.push(editor)
    button.addEventListener('click', function () { form.hidden = false; body.hidden = true; button.hidden = true; input.focus() })
    input.addEventListener('input', updateStatus)
    panel.querySelector('[data-debrief-apply]').addEventListener('click', editor.apply)
    panel.querySelector('[data-debrief-cancel]').addEventListener('click', function () { input.value = editor.applied; close(); updateStatus() })
  })
  return Object.assign({}, handlers, {
    onAction: function (action) {
      if (!handlers || typeof handlers.onAction !== 'function') return
      if (action.requestContext && action.requestContext.stepKey === 'DebriefValidateReview') {
        // The global save includes editors still open, including hidden chapters.
        if (editors.some(function (editor) { return !editor.apply() })) return
        var requestContext = Object.assign({}, action.requestContext)
        if (Object.keys(changes).length) {
          if (!ui.reviewIdentity) { status('Rafraîchis ce bilan pour pouvoir enregistrer ses modifications.'); return }
          requestContext.debriefChapterEdits = {
            activityId: ui.reviewIdentity.activityId,
            generatedAt: ui.reviewIdentity.generatedAt,
            chapters: Object.keys(changes).map(function (key) { return { key: key, markdown: changes[key] } })
          }
        }
        action = Object.assign({}, action, { requestContext: requestContext })
      }
      handlers.onAction(action)
    }
  })
}
